/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {TuiDay} from '@taiga-ui/cdk';
import {Subscription} from 'rxjs';

type BankSlipRuleGroup = 'interest' | 'fine' | 'discount';

@Component({
  selector: 'gatewee-payment-rules',
  templateUrl: './gatewee-payment-rules.component.html',
  styleUrls: ['./gatewee-payment-rules.component.css'],
  standalone: false,
})
export class GateweePaymentRulesComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() paymentRulesFormGroup!: any;
  @Input() maxInstallments!: any;

  @Input() mode: 'invoice' | 'checkout' = 'checkout';

  /**
   * When false, hides rule toggles. `bankSlip` covers both `bank_slip` and `slipix` (same rules engine).
   */
  @Input() allowedRuleTypes: {bankSlip: boolean; creditCard: boolean} = {
    bankSlip: true,
    creditCard: true,
  };
  private prevAllowedRuleTypes: {
    bankSlip: boolean;
    creditCard: boolean;
  } | null = null;
  private discountValueSub?: Subscription;
  paymentFormController: FormGroup;
  emptyPaymentRules = true;
  interestIsCurrency = false;
  fineIsCurrency = false;
  discountIsCurrency = false;
  minDate: TuiDay = (() => {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    return TuiDay.fromLocalNativeDate(date);
  })();

  constructor(private fb: FormBuilder, private translate: TranslateService) {
    this.paymentFormController = this.fb.group({
      ticket: [false],
      creditCard: [false],
    });
  }

  get interestPercentCustomMessages(): Record<string, string> {
    return {
      notMoreThan100: this.translate.instant(
        'common.paymentRules.validationMaxPercent',
      ),
    };
  }

  get creditCardForm(): FormGroup {
    return this.paymentRulesFormGroup.get('creditCard') as FormGroup;
  }

  get bankSlipForm(): FormGroup {
    return this.paymentRulesFormGroup.get('bankSlip') as FormGroup;
  }

  get interestForm(): FormGroup {
    return this.bankSlipForm.get('interest') as FormGroup;
  }

  get fineForm(): FormGroup {
    return this.bankSlipForm.get('fine') as FormGroup;
  }

  get discountForm(): FormGroup {
    return this.bankSlipForm.get('discount') as FormGroup;
  }

  /** When neither slip nor card is allowed for this checkout, hide the whole block. */
  get hasSupportedPaymentRuleTypes(): boolean {
    return this.allowedRuleTypes.bankSlip || this.allowedRuleTypes.creditCard;
  }

  ngOnInit() {
    this.paymentFormController.valueChanges.subscribe(value => {
      this.emptyPaymentRules = !(value.ticket || value.creditCard);
    });
    this.setupDiscountValidators();
  }

  ngOnDestroy(): void {
    this.discountValueSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['paymentRulesFormGroup'] && this.paymentRulesFormGroup) {
      this.setupDiscountValidators();
    }
    if (!changes['allowedRuleTypes'] || !this.paymentFormController) return;
    const a = this.allowedRuleTypes;
    if (
      this.prevAllowedRuleTypes &&
      this.prevAllowedRuleTypes.bankSlip === a.bankSlip &&
      this.prevAllowedRuleTypes.creditCard === a.creditCard
    ) {
      return;
    }
    this.prevAllowedRuleTypes = {
      bankSlip: a.bankSlip,
      creditCard: a.creditCard,
    };
    const patch: {ticket?: boolean; creditCard?: boolean} = {};
    if (!a.bankSlip) patch.ticket = false;
    if (!a.creditCard) patch.creditCard = false;
    if (Object.keys(patch).length > 0) {
      this.paymentFormController.patchValue(patch, {emitEvent: true});
    }
  }

  onCurrencyToggle(group: BankSlipRuleGroup, isCurrency: boolean) {
    const form =
      group === 'interest'
        ? this.interestForm
        : group === 'fine'
        ? this.fineForm
        : this.discountForm;

    if (group === 'interest') this.interestIsCurrency = isCurrency;
    if (group === 'fine') this.fineIsCurrency = isCurrency;
    if (group === 'discount') this.discountIsCurrency = isCurrency;

    form.get('type')?.setValue(isCurrency ? 'amount' : 'rate');

    const valueCtrl = form.get('value');
    if (!isCurrency) {
      valueCtrl?.setValidators([
        Validators.required,
        this.maxPercentValidator(100),
      ]);
    } else {
      valueCtrl?.clearValidators();
    }
    valueCtrl?.updateValueAndValidity();
  }

  maxPercentValidator(max: number) {
    return (control: AbstractControl) => {
      const value = Number(control.value);

      if (isNaN(value)) return null;

      return value > max ? {notMoreThan100: true} : null;
    };
  }

  /**
   * When a discount value is set, untilDate is required so serializers cannot
   * silently drop the discount on submit. Errors are set on untilDate so the
   * datepicker can surface them.
   */
  private syncDiscountUntilDateValidators(): void {
    const discount = this.discountForm;
    if (!discount) {
      return;
    }
    const valueCtrl = discount.get('value');
    const untilCtrl = discount.get('untilDate');
    if (!valueCtrl || !untilCtrl) {
      return;
    }
    if (this.hasDiscountValue(valueCtrl.value)) {
      untilCtrl.setValidators([Validators.required]);
    } else {
      untilCtrl.clearValidators();
    }
    untilCtrl.updateValueAndValidity({emitEvent: false});
  }

  private hasDiscountValue(value: unknown): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    if (typeof value === 'number') {
      return !Number.isNaN(value) && value > 0;
    }
    const s = String(value)
      .replace(/\s/g, '')
      .replace(/R\$\s?/g, '')
      .replace(/\./g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return !Number.isNaN(n) && n > 0;
  }

  private setupDiscountValidators(): void {
    this.discountValueSub?.unsubscribe();
    const discount = this.discountForm;
    if (!discount) {
      return;
    }
    this.syncDiscountUntilDateValidators();
    this.discountValueSub = discount.valueChanges.subscribe(() => {
      this.syncDiscountUntilDateValidators();
    });
  }
}
